import React, { useRef, useEffect, useState } from 'react';

import "../../Styles/adminPlayer.css";
import { FaPlay, FaPause } from 'react-icons/fa';
import { GrForwardTen, GrBackTen } from "react-icons/gr";
import { GoDotFill } from "react-icons/go";
import { BsArrowsFullscreen, BsFullscreenExit } from 'react-icons/bs';
import AdminProcessButton from './AdminProcessButton';
import AdminCustomURLCopy from './AdminCustomURLCopy';

const AdminVideoPlayer = ({ options, className, data, id, custom }) => {
    const videoNode = useRef(null);
    const player = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false); // To track the play/pause state
    const [isFullscreen, setIsFullscreen] = useState(false);
    const togglePlayPause = () => {
        if (player.current) {
            if (player.current.paused()) {
                player.current.play().catch((error) => {
                    console.error('Error while trying to play video', error);
                });
                setIsPlaying(true);
            } else {
                player.current.pause();
                setIsPlaying(false);
            }
        }
    };
    const handleFullscreen = () => {
        if (player.current) {
            if (player.current.isFullscreen()) {
                player.current.exitFullscreen();
            } else {
                player.current.requestFullscreen();
            }
        }
    }
    const skipForward = (duration) => {
        if (player.current) {
            player.current.currentTime(player.current.currentTime() + duration)
        }
    };
    const skipBackward = (duration) => {
        if (player.current) {
            player.current.currentTime(player.current.currentTime() - duration)
        }
    };

    // Extract source with fallback logic
    const primarySrc = options?.sources?.[0]?.src;
    const fallbackSrc = data?.video_url || data?.episode_url || data?.mp4_url || data?.m3u8_url || data?.trailer_source_url || data?.trailer_m3u8_url || data?.trailer_mp4_url;
    const finalSrc = primarySrc || fallbackSrc;

    const videoType = options?.sources?.[0]?.type || (finalSrc?.endsWith('.m3u8') ? 'application/x-mpegURL' : 'video/mp4');

    // Initialize player once
    useEffect(() => {
        let playerInstance;

        const initPlayer = async () => {
            if (videoNode.current && !player.current) {
                try {
                    // Dynamically import video.js and plugins
                    const [videojsModule] = await Promise.all([
                        import('video.js'),
                        import('video.js/dist/video-js.css'),
                        import('videojs-hls-quality-selector/dist/videojs-hls-quality-selector.css'),
                        import('videojs-hls-quality-selector')
                    ]);
                    const vjs = videojsModule.default || videojsModule;

                    if (!videoNode.current) return;

                    const updatedOptions = custom ? {} : {
                        controls: true,
                        muted: false,
                        autoplay: false,
                        responsive: true,
                        breakpoints: [{ tiny: 300, xsmall: 400, small: 500, medium: 600, large: 700, xlarge: 800, huge: 900 }],
                        textTrackSettings: false,
                        controlBar: {
                            audioTrackButton: false,
                            subtitlesButton: false,
                            fullscreenToggle: false,
                            volumePanel: { inline: false },
                            children: [
                                "playToggle",
                                "durationDisplay",
                                "skipBackward",
                                "timeDivider",
                                "currentTimeDisplay",
                                "progressControl",
                                "remainingTimeDisplay",
                                "playbackRateMenuButton",
                                "pictureInPictureToggle",
                                "volumeMenuButton",
                            ],
                        },
                    };

                    playerInstance = vjs(videoNode.current, {
                        ...options,
                        ...updatedOptions
                    });
                    player.current = playerInstance;

                    playerInstance.on('play', () => setIsPlaying(true));
                    playerInstance.on('pause', () => setIsPlaying(false));
                    playerInstance.on('fullscreenchange', () => setIsFullscreen(playerInstance.isFullscreen()));
                    
                    // Initial HLS quality selector setup if applicable
                    if (options?.sources?.[0]?.src?.endsWith('.m3u8')) {
                        if (typeof playerInstance.hlsQualitySelector === 'function') {
                            playerInstance.hlsQualitySelector({ displayCurrentQuality: true });
                        }
                    }
                } catch (error) {
                    console.error('Video.js initialization failed:', error);
                }
            }
        };

        initPlayer();

        return () => {
            if (player.current) {
                player.current.dispose();
                player.current = null;
            }
        };
    }, [custom]); // Only re-init if 'custom' mode changes

    // Update source reactively without re-initializing
    useEffect(() => {
        if (player.current && finalSrc) {
            player.current.src({ src: finalSrc, type: videoType });
            if (options?.poster) {
                player.current.poster(options.poster);
            }
            // Re-apply quality selector if source changed to HLS
            if (finalSrc.endsWith('.m3u8') && typeof player.current.hlsQualitySelector === 'function') {
                try {
                    player.current.hlsQualitySelector({ displayCurrentQuality: true });
                } catch (e) { /* ignore */ }
            }
        }
    }, [finalSrc, videoType, options?.poster]);

    // Handle UI visibility based on user activity
    useEffect(() => {
        if (!player.current) return;
        
        const onUserInactive = () => {
            const container = videoNode.current?.closest('.admincommonplayer');
            if (container) {
                const elements = container.querySelectorAll('.play-skip-button-center, .custom-fullscreen-button');
                elements.forEach(el => el.style.display = 'none');
            }
        };

        const onUserActive = () => {
            const container = videoNode.current?.closest('.admincommonplayer');
            if (container) {
                const elements = container.querySelectorAll('.play-skip-button-center, .custom-fullscreen-button');
                elements.forEach(el => el.style.display = 'flex');
            }
        };

        player.current.on('userinactive', onUserInactive);
        player.current.on('useractive', onUserActive);

        return () => {
            if (player.current) {
                player.current.off('userinactive', onUserInactive);
                player.current.off('useractive', onUserActive);
            }
        };
    }, [finalSrc]); 

    return (
        <>
            {finalSrc ? (
                <>
                    <div 
                        data-vjs-player 
                        className={`position-relative mb-4 admincommonplayer ${className || ''} adminVideoPlayer vjs-16-9 overflow-hidden rounded-4`} 
                        style={{ backgroundColor: '#000', minHeight: '200px', display: 'block' }}
                    >
                        <p className='position-absolute playout-live'><GoDotFill />LIVE</p>
                        <div className="position-absolute col-6 play-skip-button-center align-items-center justify-content-between">
                            <button className="backward-btn bg-transparent" onClick={() => skipBackward(10)}>
                                <GrBackTen style={{ fontSize: "38px" }} />
                            </button>
                            <button className={isPlaying ? "bg-transparent" : "bg-transparent ms-2"} onClick={togglePlayPause}>
                                {isPlaying ? <FaPause style={{ fontSize: "35px" }} /> : <FaPlay style={{ fontSize: "35px" }} />}
                            </button>
                            <button className="forward-btn bg-transparent" onClick={() => skipForward(10)}>
                                <GrForwardTen style={{ fontSize: "38px" }} />
                            </button>
                        </div>
                        <button className="custom-fullscreen-button bg-transparent" onClick={handleFullscreen}>
                            {isFullscreen ? <BsFullscreenExit style={{ fontSize: "20px" }} /> : <BsArrowsFullscreen style={{ fontSize: "20px" }} />}
                        </button>
                        <video 
                            data-vjs-player 
                            ref={videoNode} 
                            className={`video-js vjs-default-skin vjs-16-9 w-100 h-100`} 
                            id={id}
                            poster={options?.poster}
                            playsInline
                        >
                            <source src={finalSrc} type={videoType} />
                        </video>
                    </div>
                    <AdminCustomURLCopy link={finalSrc} fieldName={""} className="copy-board-input mt-2" labelClassName="mt-md-2" />
                </>
            ) : (
                (data?.type == "transcoding_video" || data?.type == "m3u8_url") && !data?.m3u8_url ? (
                    <AdminProcessButton cell={data?.current_status} row={{ ...data }} />
                ) : (
                    <div className="adminVideoPlayer vjs-16-9 rounded-4 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#000', minHeight: '200px' }}>
                        <p className="text-white-50 text-center">No video source available</p>
                    </div>
                )
            )}
        </>
    );
};

export default AdminVideoPlayer;
